import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { DashboardData, ClientOverview, GeneralInsights, ClientVoiceQuote, TimelineItem, ProjectStatus } from '../types';
import { apiService } from '../services/api';

interface UseDashboardReturn {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateClientOverview: (updatedOverview: ClientOverview) => void;
  updateGeneralInsights: (updatedInsights: GeneralInsights) => Promise<void>;
  updateClientVoice: (updatedVoices: ClientVoiceQuote[]) => Promise<void>;
  updateTimeline: (updatedTimeline: TimelineItem[]) => Promise<void>;
  updateProjectStatus: (updatedProjectStatus: ProjectStatus) => Promise<void>;
  updateAISummary: (newSummary: string) => Promise<void>;
}

export const useDashboard = (clientId?: string): UseDashboardReturn => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!clientId) {
        setError('Client ID is required');
        setLoading(false);
        return;
      }
      
      // Get Auth0 token and set it in API service
      const token = await getAccessTokenSilently();
      apiService.setAuthToken(token);
      
      const dashboardData = await apiService.getDashboard(clientId);
      setData(dashboardData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId, getAccessTokenSilently]);

  // Helper function to refetch client overview and update timestamp
  const refreshClientOverview = useCallback(async () => {
    try {
      if (!clientId) return;
      
      const token = await getAccessTokenSilently();
      apiService.setAuthToken(token);
      
      const updatedOverview = await apiService.getClientOverview(clientId);
      setData(prevData => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          clientOverview: updatedOverview
        };
      });
    } catch (err) {
      console.error('Error refreshing client overview:', err);
    }
  }, [clientId, getAccessTokenSilently]);

  const updateClientOverview = (updatedOverview: ClientOverview) => {
    setData(prevData => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        clientOverview: updatedOverview
      };
    });
  };

  const updateGeneralInsights = async (updatedInsights: GeneralInsights) => {
    setData(prevData => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        generalInsights: updatedInsights
      };
    });
    // Refresh client overview to get updated timestamp
    await refreshClientOverview();
  };

  const updateClientVoice = async (updatedVoices: ClientVoiceQuote[]) => {
    setData(prevData => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        clientVoice: updatedVoices
      };
    });
    // Refresh client overview to get updated timestamp
    await refreshClientOverview();
  };

  const updateTimeline = async (updatedTimeline: TimelineItem[]) => {
    setData(prevData => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        timeline: updatedTimeline
      };
    });
    // Refresh client overview to get updated timestamp
    await refreshClientOverview();
  };

  const updateProjectStatus = async (updatedProjectStatus: ProjectStatus) => {
    setData(prevData => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        projectStatus: updatedProjectStatus
      };
    });
    // Refresh client overview to get updated timestamp
    await refreshClientOverview();
  };

  const updateAISummary = async (newSummary: string) => {
    setData(prevData => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        aiSummary: newSummary
      };
    });
    // Refresh client overview to get updated timestamp
    await refreshClientOverview();
  };

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId, fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    updateClientOverview,
    updateGeneralInsights,
    updateClientVoice,
    updateTimeline,
    updateProjectStatus,
    updateAISummary
  };
}; 